import { Timestamp } from 'firebase/firestore';

import type { ElasticQueryFilters } from '@src/models/interfaces/general/ElasticQueryParams.interface';
import type {
  ElasticSearchHit,
  ElasticSearchResponse,
} from '@src/models/interfaces/general/ElasticSearchResponseInterface';
import type {
  IExternalUserInfo,
  IUserFormData,
  IUserJob,
} from '@src/models/interfaces/user-form.interface';
import type {
  TUserSummary,
  UserElasticSearchIdSource,
  UserNotificationTarget,
  UserNotificationTargetDTO,
  UserSearchIdsResult,
} from '@src/models/interfaces/user.interface';
import type {
  ITUserCreationParams,
  ITUserUpdateParams,
} from '@src/models/interfaces/requests/user';
import { trackingWritePayload } from '@src/packages/users/utils/trackingPolicy';
import { escapeQueryStringValue } from '@src/util/elastic/escapeQueryStringValue';

export type UserListState = 'all' | 'enabled' | 'disabled' | 'pending';

/** Indexed user fields that `searchTerm` can match via `toListElasticFilters`. */
export type UserListSearchField = 'firstName' | 'lastName' | 'email' | 'idCard';

const USER_LIST_SEARCH_FIELD_TO_ELASTIC: Record<UserListSearchField, string> = {
  firstName: 'firstName',
  lastName: 'lastName',
  email: 'email.keyword',
  idCard: 'idCard',
};

export interface UserListElasticFiltersParams {
  searchTerm?: string;
  email?: string;
  state?: UserListState;
  exactMatch?: boolean;
  /**
   * Fields OR-matched by `searchTerm` (default: firstName + lastName).
   * Use e.g. `['firstName', 'lastName', 'email', 'idCard']` for a single search box.
   */
  searchFields?: UserListSearchField[];
  extraFilters?: Partial<ElasticQueryFilters>;
}

export class UserMapper {
  /**
   * Maps user form data to create payload for Cloud Function
   *
   * Transforms:
   * - IUserFormData → ITUserCreationParams (without organization)
   * - Formats ID card (removes dots)
   * - Parses job information
   * - Converts birth date to Timestamp
   *
   * Note: organization is handled separately in Service layer
   *
   * @param formData Raw form data from component
   * @param job Parsed job information
   * @param externalUserInfo External user information (company, access)
   * @param birthDate Original birth date to convert to Timestamp
   * @returns Fully prepared ITUserCreationParams (payload for Cloud Function)
   */
  static mapToCreatePayload(
    formData: IUserFormData,
    job: IUserJob,
    externalUserInfo: IExternalUserInfo | null,
    _birthDate?: Date | string,
  ): ITUserCreationParams {
    const tracking = trackingWritePayload(
      formData.trackingMode ?? 'off',
      formData.trackingPrecision ?? 'coarse',
    );

    return {
      id: formData.id,
      email: formData.email,
      access: formData.access,
      divisions: formData.divisions || [],
      job,
      externalUserInfo,
      locale: formData.locale || 'es',
      // Personal profile is collected on invite acceptance — omit from admin invite.
      trackingMode: tracking.trackingMode,
      trackingPrecision: tracking.trackingPrecision,
      isTracked: tracking.isTracked,
    };
  }

  /**
   * Maps user form data to update payload for Cloud Function
   *
   * Org membership fields only (access, divisions, job, company).
   *
   * @param formData Raw form data from component
   * @param job Parsed job information
   * @param externalUserInfo External user information (company, access)
   * @param birthDate Unused for admin update (kept for call-site compatibility)
   * @returns Fully prepared ITUserUpdateParams for update
   */
  static mapToUpdatePayload(
    formData: IUserFormData,
    job: IUserJob,
    externalUserInfo: IExternalUserInfo | null,
    birthDate?: Date | string,
  ): ITUserUpdateParams {
    return {
      ...this.mapToCreatePayload(formData, job, externalUserInfo, birthDate),
      flexTime: formData.flexTime ?? false,
      professionalEmail: formData.professionalEmail || '',
      timeZone: formData.timeZone || undefined,
      ...(formData.address ? { address: formData.address } : {}),
      ...(formData.jurisdictionId !== undefined
        ? { jurisdictionId: formData.jurisdictionId }
        : {}),
    };
  }

  /**
   * Converts Date or string to Firebase Timestamp
   *
   * @param date Date string or Date object
   * @returns Firebase Timestamp
   */
  static convertDateToTimestamp(date: Date | string): Timestamp {
    return Timestamp.fromDate(new Date(date));
  }

  static fromNotificationTargetDTO(dto: UserNotificationTargetDTO): UserNotificationTarget {
    return {
      ...UserMapper.toNotificationSummary(dto),
      divisions: dto.divisions,
    };
  }

  /**
   * Builds the summary persisted on notification subscriber documents. Mirrors the
   * field set the backend writes through `getUserSummary` for the same collection,
   * since `SubscriptionService` reads `email` (recipient) and `locale` (template).
   * `divisions` is deliberately excluded: it is not part of `TUserSummary`.
   */
  static toNotificationSummary(user: UserNotificationTarget): TUserSummary {
    return {
      birthDate: user.birthDate,
      displayName: user.displayName,
      email: user.email,
      firstName: user.firstName,
      flexTime: user.flexTime,
      id: user.id,
      idCard: user.idCard,
      lastName: user.lastName,
      locale: user.locale,
      phone: user.phone,
      photoURL: user.photoURL,
      professionalEmail: user.professionalEmail,
    };
  }

  /**
   * Validates that all required user fields are present
   *
   * @param formData User form data to validate
   * @returns true if valid, false otherwise
   */
  static isValidUserData(formData: IUserFormData): boolean {
    if (!formData || typeof formData !== 'object') return false;

    const requiredFields: (keyof IUserFormData)[] = ['email', 'access', 'organization'];

    return requiredFields.every(field => {
      const value = formData[field];
      return value !== undefined && value !== null && value !== '';
    });
  }

  /**
   * Merges user data with defaults
   *
   * Ensures all optional fields have sensible defaults
   *
   * @param formData Partial user data
   * @returns Complete user data with defaults
   */
  static withDefaults(formData: Partial<IUserFormData>): IUserFormData {
    return {
      email: formData.email || '',
      firstName: formData.firstName || '',
      lastName: formData.lastName || '',
      idCard: formData.idCard || '',
      birthDate: formData.birthDate || new Date(),
      phone: formData.phone || null,
      flexTime: formData.flexTime ?? false,
      professionalEmail: formData.professionalEmail || null,
      locale: formData.locale || 'es',
      password: formData.password,
      timeZone: formData.timeZone || null,
      access: formData.access || 'user',
      divisions: formData.divisions || [],
      job: formData.job || { id: '', name: '' },
      organization: formData.organization || { id: '' },
      company: formData.company,
      accessReports: formData.accessReports ?? false,
      address: formData.address || {
        address: '',
        city: '',
        country: '',
        region: '',
        zip: '',
        location: null,
      },
      id: formData.id,
    } as IUserFormData;
  }

  /**
   * Builds a partial, case-insensitive query_string clause (e.g. "ric" matches
   * "ricardo"). query_string wildcards match per token, so each whitespace-
   * separated word becomes its own clause and they are AND-ed — this is what
   * lets a multi-word term like "ricardo perez" match across separate tokens.
   * When several fields are given, each word matches any of them (OR), e.g.
   * "ricardo" can hit `firstName` and "perez" `lastName`.
   */
  private static buildPartialMatch(fields: UserListSearchField[], value: string): string {
    const tokenClauses = value
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .map(token => {
        const perField = fields.map(field => {
          const elasticField = USER_LIST_SEARCH_FIELD_TO_ELASTIC[field];
          const normalizedToken = field === 'idCard' ? token.replace(/\./g, '') : token;
          const escaped = escapeQueryStringValue(normalizedToken);
          return `${elasticField}:*${escaped}*`;
        });
        return perField.length === 1 ? perField[0] : `(${perField.join(' OR ')})`;
      });

    if (!tokenClauses.length) return '';
    return tokenClauses.length === 1 ? tokenClauses[0] : `(${tokenClauses.join(' AND ')})`;
  }

  /**
   * Builds the account-state filter, mirroring the client-side logic:
   * - enabled:  !disabled && onboardingCompleted !== false  (active, onboarded)
   * - disabled: disabled === true
   * - pending:  onboardingCompleted === false
   *
   * `disabled` and `pending` are exact boolean equalities, so they use a `term`
   * filter. `enabled` uses query_string negation so legacy documents without
   * these fields still match: `NOT disabled:true` covers missing/false, and
   * `NOT onboardingCompleted:false` covers missing/true.
   */
  private static buildStateFilter(
    state?: UserListState,
  ): { term?: Record<string, unknown>; textClause?: string } {
    switch (state) {
      case 'enabled':
        return { textClause: 'NOT disabled:true AND NOT onboardingCompleted:false' };
      case 'disabled':
        return { term: { disabled: true } };
      case 'pending':
        return { term: { onboardingCompleted: false } };
      default:
        return {};
    }
  }

  /**
   * Builds Elastic filters for the user list query (name, email, account state).
   * Email uses an exact `term` on `email.keyword` so characters like `+` / `@`
   * are matched literally (unlike query_string).
   */
  static toListElasticFilters({
    searchTerm = '',
    email = '',
    state = 'all',
    exactMatch = false,
    searchFields = ['firstName', 'lastName'],
    extraFilters = {},
  }: UserListElasticFiltersParams = {}): ElasticQueryFilters {
    const filters: ElasticQueryFilters = { ...extraFilters };

    const stateFilter = UserMapper.buildStateFilter(state);
    const trimmedEmail = email.trim();
    if (stateFilter.term || trimmedEmail) {
      filters.term = {
        ...filters.term,
        ...stateFilter.term,
        ...(trimmedEmail ? { 'email.keyword': trimmedEmail } : {}),
      };
    }

    const fields: UserListSearchField[] =
      searchFields.length > 0 ? searchFields : ['firstName', 'lastName'];
    const clauses: string[] = [];
    if (searchTerm) {
      clauses.push(
        exactMatch
          ? `(${fields.map(field => `${USER_LIST_SEARCH_FIELD_TO_ELASTIC[field]}:"${searchTerm}"`).join(' OR ')})`
          : UserMapper.buildPartialMatch(fields, searchTerm),
      );
    }
    if (stateFilter.textClause) clauses.push(stateFilter.textClause);
    if (clauses.length) filters.textSearch = clauses.filter(Boolean).join(' AND ');

    return filters;
  }

  static fromElasticSearchIdHit = (
    hit: ElasticSearchHit<UserElasticSearchIdSource>,
  ): string | null => {
    const id = hit._source?.id ?? hit._id;
    return id || null;
  };

  static fromElasticSearchIdsResponse = (
    response: ElasticSearchResponse<UserElasticSearchIdSource> | null,
  ): UserSearchIdsResult => {
    const hits = response?.hits?.hits ?? [];
    const ids = hits
      .map(hit => UserMapper.fromElasticSearchIdHit(hit))
      .filter((id): id is string => id != null);

    return {
      ids,
      total: response?.hits?.total?.value ?? ids.length,
    };
  };
}

export default UserMapper;
